// ─── 编程工具函数 ────────────────────────────────────────────────────────────────

// ─── JWT ─────────────────────────────────────────────────────────────────────

export function parseJwt(token) {
  const parts = token.trim().split('.');
  if (parts.length !== 3) throw new Error('JWT 格式无效，应为 header.payload.signature');
  const decode = (s) => {
    const b64 = s.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(s.length / 4) * 4, '=');
    return JSON.parse(atob(b64));
  };
  const header = decode(parts[0]);
  const payload = decode(parts[1]);
  const now = Math.floor(Date.now() / 1000);
  const expired = payload.exp ? payload.exp < now : null;
  return { header, payload, signature: parts[2], expired, expiresAt: payload.exp ? new Date(payload.exp * 1000).toLocaleString('zh-CN') : null };
}

export function generateJwt(payload, secret = 'secret') {
  const header = { alg: 'HS256', typ: 'JWT' };
  const encode = (obj) => btoa(JSON.stringify(obj)).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
  const h = encode(header);
  const p = encode(typeof payload === 'string' ? JSON.parse(payload) : payload);
  // Note: real HMAC-SHA256 requires crypto API; this is a demo signature
  const sig = encode({ demo: 'signature_requires_crypto_api' });
  return `${h}.${p}.${sig}`;
}

// ─── 正则表达式测试 ────────────────────────────────────────────────────────────

export function testRegex(pattern, flags, text) {
  const re = new RegExp(pattern, flags);
  const matches = [];
  let m;
  const globalRe = new RegExp(pattern, flags.includes('g') ? flags : flags + 'g');
  while ((m = globalRe.exec(text)) !== null) {
    matches.push({
      match: m[0],
      index: m.index,
      groups: m.slice(1),
      namedGroups: m.groups || {},
    });
    if (!flags.includes('g')) break;
  }
  const highlighted = text.replace(globalRe, '<mark>$&</mark>');
  return { matches, count: matches.length, highlighted, isMatch: re.test(text) };
}

// ─── UUID / ULID / NanoID ─────────────────────────────────────────────────────

export function generateUuid(version = 4) {
  if (version === 4) {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0;
      return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
    });
  }
  if (version === 1) {
    // Pseudo v1 (not spec-compliant but demonstrates format)
    const now = Date.now();
    const hex = now.toString(16).padStart(12, '0');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-1xxx-yxxx-xxxxxxxxxxxx`.replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0;
      return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
    });
  }
  return generateUuid(4);
}

export function generateUlid() {
  const CHARS = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
  const ts = Date.now();
  let t = '', r = '';
  let n = ts;
  for (let i = 9; i >= 0; i--) { t = CHARS[n % 32] + t; n = Math.floor(n / 32); }
  for (let i = 0; i < 16; i++) r += CHARS[Math.floor(Math.random() * 32)];
  return t + r;
}

export function generateNanoid(size = 21) {
  const CHARS = 'useandom-26T198340PX75pxJACKVERYMINDBUSHWOLF_GQZbfghjklqvwyzrict';
  return Array.from({ length: size }, () => CHARS[Math.floor(Math.random() * 64)]).join('');
}

export function parseUuid(uuid) {
  const clean = uuid.replace(/-/g, '');
  if (!/^[0-9a-fA-F]{32}$/.test(clean)) throw new Error('无效的 UUID 格式');
  const version = parseInt(clean[12], 16);
  const variant = parseInt(clean[16], 16);
  const variantStr = (variant & 0xC) === 0x8 ? 'RFC 4122' : (variant & 0x8) === 0 ? 'NCS' : 'Microsoft';
  return { uuid, version, variant: variantStr, hex: clean, urn: `urn:uuid:${uuid.toLowerCase()}` };
}

// ─── 进制转换 ─────────────────────────────────────────────────────────────────

export function baseConvert(value, fromBase, toBase) {
  const n = parseInt(String(value).replace(/\s/g, ''), fromBase);
  if (isNaN(n)) throw new Error('无效的输入值');
  return {
    binary: n.toString(2),
    octal: n.toString(8),
    decimal: n.toString(10),
    hex: n.toString(16).toUpperCase(),
    base32: n.toString(32).toUpperCase(),
    base36: n.toString(36).toUpperCase(),
    result: n.toString(toBase),
  };
}

// ─── 浮点数进制转换 ────────────────────────────────────────────────────────────

export function floatBaseConvert(value) {
  const n = parseFloat(value);
  if (isNaN(n)) throw new Error('无效的浮点数');
  const buf = new ArrayBuffer(8);
  new Float64Array(buf)[0] = n;
  const view = new DataView(buf);
  const bytes = Array.from({ length: 8 }, (_, i) => view.getUint8(i));
  const hex = bytes.map(b => b.toString(16).padStart(2, '0')).join(' ');
  const binary = bytes.map(b => b.toString(2).padStart(8, '0')).join(' ');
  const sign = (view.getUint8(7) >> 7) & 1;
  const exp = ((view.getUint8(7) & 0x7F) << 4) | ((view.getUint8(6) >> 4) & 0x0F);
  return { value: n, hex, binary, sign, exponent: exp - 1023, bytes: bytes.map(b => '0x' + b.toString(16).padStart(2, '0')) };
}

// ─── 位运算计算器 ──────────────────────────────────────────────────────────────

export function bitwiseCalc(a, b, op) {
  const na = parseInt(String(a));
  const nb = parseInt(String(b));
  let result;
  switch (op) {
    case 'AND': result = na & nb; break;
    case 'OR': result = na | nb; break;
    case 'XOR': result = na ^ nb; break;
    case 'NOT': result = ~na; break;
    case 'LSHIFT': result = na << nb; break;
    case 'RSHIFT': result = na >> nb; break;
    case 'URSHIFT': result = na >>> nb; break;
    default: throw new Error('未知操作');
  }
  return {
    a: na, b: nb, op, result,
    a_bin: (na >>> 0).toString(2).padStart(32, '0'),
    b_bin: (nb >>> 0).toString(2).padStart(32, '0'),
    result_bin: (result >>> 0).toString(2).padStart(32, '0'),
    result_hex: '0x' + (result >>> 0).toString(16).toUpperCase(),
  };
}

// ─── Chmod 计算器 ─────────────────────────────────────────────────────────────

export function chmodCalc(octal) {
  const n = parseInt(String(octal), 8);
  if (isNaN(n) || n < 0 || n > 777) throw new Error('请输入 0-777 的八进制权限值');
  const perms = (n) => ({
    read:    !!(n & 4),
    write:   !!(n & 2),
    execute: !!(n & 1),
    str: (n & 4 ? 'r' : '-') + (n & 2 ? 'w' : '-') + (n & 1 ? 'x' : '-'),
  });
  const o = parseInt(String(octal));
  const owner = perms(Math.floor(o / 100) % 10);
  const group = perms(Math.floor(o / 10) % 10);
  const other = perms(o % 10);
  return {
    octal: String(octal).padStart(3, '0'),
    symbolic: owner.str + group.str + other.str,
    owner, group, other,
    decimal: n,
    hex: '0x' + n.toString(16),
  };
}

// ─── SQL 压缩 / 格式化 ─────────────────────────────────────────────────────────

export function sqlFormat(sql) {
  const keywords = ['SELECT', 'FROM', 'WHERE', 'JOIN', 'LEFT JOIN', 'RIGHT JOIN', 'INNER JOIN', 'ON', 'AND', 'OR', 'ORDER BY', 'GROUP BY', 'HAVING', 'LIMIT', 'INSERT INTO', 'VALUES', 'UPDATE', 'SET', 'DELETE FROM', 'CREATE TABLE', 'ALTER TABLE', 'DROP TABLE', 'INDEX', 'UNION'];
  let result = sql.trim();
  for (const kw of keywords) {
    result = result.replace(new RegExp(`\\b${kw}\\b`, 'gi'), `\n${kw}`);
  }
  return result.replace(/\n+/g, '\n').trim();
}

export function sqlMinify(sql) {
  return sql.replace(/--[^\n]*/g, '').replace(/\/\*[\s\S]*?\*\//g, '').replace(/\s+/g, ' ').trim();
}

// ─── HTML 转义 ────────────────────────────────────────────────────────────────

export function htmlEscape(text) {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

export function htmlUnescape(text) {
  return text.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'");
}

// ─── CSS 格式化 ───────────────────────────────────────────────────────────────

export function cssFormat(css) {
  return css
    .replace(/\s*{\s*/g, ' {\n  ')
    .replace(/;\s*/g, ';\n  ')
    .replace(/\s*}\s*/g, '\n}\n')
    .replace(/\n {2}\n/g, '\n')
    .trim();
}

export function cssMinify(css) {
  return css
    .replace(/\/\*[\s\S]*?\*\//g, '')
    .replace(/\s+/g, ' ')
    .replace(/\s*{\s*/g, '{')
    .replace(/\s*}\s*/g, '}')
    .replace(/\s*:\s*/g, ':')
    .replace(/\s*;\s*/g, ';')
    .replace(/;}/g, '}')
    .trim();
}

// ─── XML 格式化 ───────────────────────────────────────────────────────────────

export function xmlFormat(xml) {
  let formatted = '', indent = 0;
  xml.split(/>\s*</).forEach(node => {
    if (node.match(/^\/\w/)) indent--;
    formatted += '  '.repeat(Math.max(indent, 0)) + '<' + node + '>\n';
    if (node.match(/^<?\w[^/]*[^/]$/) && !node.endsWith('/')) indent++;
  });
  return formatted.trim().replace(/^<|>$/g, '').replace(/<>/g, '');
}

export function xmlMinify(xml) {
  return xml.replace(/>\s+</g, '><').replace(/\s+/g, ' ').trim();
}

// ─── JavaScript 格式化 ────────────────────────────────────────────────────────

export function jsFormat(code) {
  let indent = 0, result = '', i = 0;
  const pad = () => '  '.repeat(indent);
  while (i < code.length) {
    const c = code[i];
    if (c === '{' || c === '[') {
      result += c + '\n'; indent++; result += pad();
    } else if (c === '}' || c === ']') {
      indent = Math.max(0, indent - 1);
      result += '\n' + pad() + c;
    } else if (c === ';') {
      result += c + '\n' + pad();
    } else if (c === ',') {
      result += c + '\n' + pad();
    } else {
      result += c;
    }
    i++;
  }
  return result;
}

export function jsMinify(code) {
  return code.replace(/\/\/[^\n]*/g, '').replace(/\/\*[\s\S]*?\*\//g, '').replace(/\s+/g, ' ').trim();
}

// ─── Curl 转代码 ──────────────────────────────────────────────────────────────

export function curlToCode(curl, lang = 'javascript') {
  const methodM = curl.match(/-X\s+(\w+)/i);
  const method = methodM ? methodM[1].toUpperCase() : 'GET';
  const urlM = curl.match(/curl\s+(?:-[^\s]+\s+)*['"]?([^\s'"]+)['"]?/);
  const url = urlM ? urlM[1] : '';
  const headers = {};
  const headerRe = /-H\s+['"]([^'"]+)['"]/g;
  let hm;
  while ((hm = headerRe.exec(curl)) !== null) {
    const [k, ...vParts] = hm[1].split(':');
    headers[k.trim()] = vParts.join(':').trim();
  }
  const dataM = curl.match(/(?:-d|--data)\s+['"]([^'"]+)['"]/);
  const body = dataM ? dataM[1] : null;

  if (lang === 'javascript') {
    const opts = [];
    if (method !== 'GET') opts.push(`method: '${method}'`);
    if (Object.keys(headers).length) opts.push(`headers: ${JSON.stringify(headers, null, 2)}`);
    if (body) opts.push(`body: '${body}'`);
    return `const response = await fetch('${url}'${opts.length ? `, {\n  ${opts.join(',\n  ')}\n}` : ''});\nconst data = await response.json();`;
  }
  if (lang === 'python') {
    const hStr = Object.keys(headers).length ? `\nheaders = ${JSON.stringify(headers, null, 2)}` : '';
    const dStr = body ? `\ndata = '${body}'` : '';
    return `import requests\n${hStr}${dStr}\nresponse = requests.${method.toLowerCase()}('${url}'${Object.keys(headers).length ? ', headers=headers' : ''}${body ? ', data=data' : ''})\nprint(response.json())`;
  }
  return `// ${method} ${url}\n// Headers: ${JSON.stringify(headers)}\n// Body: ${body}`;
}

// ─── 单位转换 ─────────────────────────────────────────────────────────────────

export function convertUnit(value, fromUnit, toUnit, category) {
  const v = parseFloat(value);
  if (isNaN(v)) throw new Error('请输入有效的数值');

  const conversions = {
    length: {
      m: 1, km: 1000, cm: 0.01, mm: 0.001, mile: 1609.344,
      yard: 0.9144, foot: 0.3048, inch: 0.0254, nm: 1e-9,
    },
    weight: {
      kg: 1, g: 0.001, mg: 1e-6, t: 1000, lb: 0.453592,
      oz: 0.0283495, stone: 6.35029,
    },
    temperature: { C: 'C', F: 'F', K: 'K' },
    time: {
      s: 1, ms: 0.001, us: 1e-6, min: 60, h: 3600, day: 86400, week: 604800,
    },
    data: {
      B: 1, KB: 1024, MB: 1048576, GB: 1073741824, TB: 1099511627776,
      bit: 0.125, Kbit: 128, Mbit: 131072, Gbit: 134217728,
    },
    area: {
      'm²': 1, 'km²': 1e6, 'cm²': 1e-4, 'mm²': 1e-6, acre: 4046.86, ha: 10000,
      'ft²': 0.0929, 'in²': 0.000645,
    },
    speed: {
      'm/s': 1, 'km/h': 1 / 3.6, mph: 0.44704, knot: 0.514444,
    },
  };

  if (category === 'temperature') {
    let celsius;
    if (fromUnit === 'C') celsius = v;
    else if (fromUnit === 'F') celsius = (v - 32) * 5 / 9;
    else celsius = v - 273.15;
    if (toUnit === 'C') return celsius;
    if (toUnit === 'F') return celsius * 9 / 5 + 32;
    return celsius + 273.15;
  }

  const table = conversions[category];
  if (!table) throw new Error('未知的转换类别');
  if (!(fromUnit in table) || !(toUnit in table)) throw new Error(`未知的单位: ${fromUnit} 或 ${toUnit}`);
  const base = v * table[fromUnit];
  return base / table[toUnit];
}

// ─── HTML 符号速查 ────────────────────────────────────────────────────────────

export const HTML_SYMBOLS = [
  { char: '&', entity: '&amp;', hex: '&#x26;', dec: '&#38;', name: 'Ampersand' },
  { char: '<', entity: '&lt;', hex: '&#x3C;', dec: '&#60;', name: 'Less Than' },
  { char: '>', entity: '&gt;', hex: '&#x3E;', dec: '&#62;', name: 'Greater Than' },
  { char: '"', entity: '&quot;', hex: '&#x22;', dec: '&#34;', name: 'Quotation Mark' },
  { char: "'", entity: '&apos;', hex: '&#x27;', dec: '&#39;', name: 'Apostrophe' },
  { char: '©', entity: '&copy;', hex: '&#xA9;', dec: '&#169;', name: 'Copyright' },
  { char: '®', entity: '&reg;', hex: '&#xAE;', dec: '&#174;', name: 'Registered' },
  { char: '™', entity: '&trade;', hex: '&#x2122;', dec: '&#8482;', name: 'Trademark' },
  { char: '€', entity: '&euro;', hex: '&#x20AC;', dec: '&#8364;', name: 'Euro' },
  { char: '£', entity: '&pound;', hex: '&#xA3;', dec: '&#163;', name: 'Pound' },
  { char: '¥', entity: '&yen;', hex: '&#xA5;', dec: '&#165;', name: 'Yen' },
  { char: '°', entity: '&deg;', hex: '&#xB0;', dec: '&#176;', name: 'Degree' },
  { char: '±', entity: '&plusmn;', hex: '&#xB1;', dec: '&#177;', name: 'Plus-Minus' },
  { char: '×', entity: '&times;', hex: '&#xD7;', dec: '&#215;', name: 'Multiplication' },
  { char: '÷', entity: '&divide;', hex: '&#xF7;', dec: '&#247;', name: 'Division' },
  { char: '½', entity: '&frac12;', hex: '&#xBD;', dec: '&#189;', name: 'One Half' },
  { char: '¼', entity: '&frac14;', hex: '&#xBC;', dec: '&#188;', name: 'One Quarter' },
  { char: '¾', entity: '&frac34;', hex: '&#xBE;', dec: '&#190;', name: 'Three Quarters' },
  { char: '→', entity: '&rarr;', hex: '&#x2192;', dec: '&#8594;', name: 'Right Arrow' },
  { char: '←', entity: '&larr;', hex: '&#x2190;', dec: '&#8592;', name: 'Left Arrow' },
  { char: '↑', entity: '&uarr;', hex: '&#x2191;', dec: '&#8593;', name: 'Up Arrow' },
  { char: '↓', entity: '&darr;', hex: '&#x2193;', dec: '&#8595;', name: 'Down Arrow' },
  { char: '♠', entity: '&spades;', hex: '&#x2660;', dec: '&#9824;', name: 'Spades' },
  { char: '♥', entity: '&hearts;', hex: '&#x2665;', dec: '&#9829;', name: 'Hearts' },
  { char: '♦', entity: '&diams;', hex: '&#x2666;', dec: '&#9830;', name: 'Diamonds' },
  { char: '♣', entity: '&clubs;', hex: '&#x2663;', dec: '&#9827;', name: 'Clubs' },
  { char: '…', entity: '&hellip;', hex: '&#x2026;', dec: '&#8230;', name: 'Ellipsis' },
  { char: '—', entity: '&mdash;', hex: '&#x2014;', dec: '&#8212;', name: 'Em Dash' },
  { char: '–', entity: '&ndash;', hex: '&#x2013;', dec: '&#8211;', name: 'En Dash' },
  { char: ' ', entity: '&nbsp;', hex: '&#xA0;', dec: '&#160;', name: 'Non-Breaking Space' },
];

// ─── 数学工具 ─────────────────────────────────────────────────────────────────

export function evalMathFormula(expr) {
  const sanitized = expr
    .replace(/[^0-9+\-*/()., %\s^sincotaqlgepMLPIE_]/g, '')
    .replace(/\^/g, '**')
    .replace(/Math\.sin|sin/g, 'Math.sin')
    .replace(/Math\.cos|cos/g, 'Math.cos')
    .replace(/Math\.tan|tan/g, 'Math.tan')
    .replace(/Math\.sqrt|sqrt/g, 'Math.sqrt')
    .replace(/Math\.log10|log10/g, 'Math.log10')
    .replace(/Math\.log|log/g, 'Math.log')
    .replace(/Math\.PI|PI/g, 'Math.PI')
    .replace(/Math\.E|[^Ae]e([^-+\d]|$)/g, (m) => m.replace('e', 'Math.E'));
  try {
    // eslint-disable-next-line no-new-func
    const result = new Function(`return (${sanitized})`)();
    return { result, formatted: Number.isInteger(result) ? result : result.toFixed(10).replace(/\.?0+$/, '') };
  } catch {
    throw new Error('无效的数学表达式');
  }
}

// ─── URL 解析 ─────────────────────────────────────────────────────────────────

export function parseUrl(url) {
  try {
    const u = new URL(url);
    const params = {};
    u.searchParams.forEach((v, k) => { params[k] = v; });
    return {
      href: u.href,
      protocol: u.protocol,
      host: u.host,
      hostname: u.hostname,
      port: u.port || '(default)',
      pathname: u.pathname,
      search: u.search,
      hash: u.hash,
      username: u.username,
      password: u.password,
      params,
    };
  } catch {
    throw new Error('无效的 URL 格式');
  }
}
